import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ImagePlus, X, Plus, Trash2, Eye } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useCategories } from "@/hooks/useCatalog";
import { useVendorRefresh, useVendorMe } from "@/hooks/useVendor";
import ProductPreview from "@/components/vendor/ProductPreview";
import LinkInserter from "@/components/admin/LinkInserter";
import ImageInserter from "@/components/admin/ImageInserter";
import ButtonInserter from "@/components/admin/ButtonInserter";
import DownloadsManager from "@/components/DownloadsManager";

const EMPTY = {
  name: "", slug: "", short_description: "", description: "", category_slug: "", brand: "",
  features: [], custom_specs: [], conditions: "",
  price: "", old_price: "", image_url: "", gallery: [],
  license_type: "Produit numérique", license_origin: "", duration: "Illimitée", devices: 1,
  compatibility: [], platform: "multi", delivery_mode: "instructions",
  delivery_instructions: "", delivery_time: "Après paiement", service_label: "",
  required_fields: [], warranty: "Garantie d'activation 7 jours",
  is_promo: false, promo_ends_at: "", is_featured: false, is_subscription: false,
  stock_managed: false, manual_stock: 0, tags: "", status: "draft", product_type: "",
};

const PRODUCT_TYPES = [
  ["", "Non — produit standard (sans téléchargement)"],
  ["software", "Logiciel"],
  ["game", "Jeu"],
  ["digital", "Produit numérique / Fichier"],
];

const PLATFORMS = ["windows", "mac", "multi", "android", "ios", "web"];
const DELIVERY = [
  ["license_key", "Clé de licence (stock)"],
  ["activation", "Activation manuelle (service serveur)"],
  ["instructions", "Instructions par email"],
  ["service", "Prestation de service"],
];
const FIELD_TYPES = [
  ["text", "🔤 Texte (Ligne unique)"],
  ["textarea", "📝 Texte long (Zone de texte)"],
  ["select", "📋 Liste déroulante"],
  ["radio", "🔘 Boutons radio"],
  ["checkbox", "☑️ Cases à cocher (Multi-choix)"],
  ["number", "🔢 Nombre"],
  ["email", "📧 E-mail"],
  ["tel", "📞 Téléphone / WhatsApp"],
];
const NEEDS_OPTIONS = ["select", "radio", "checkbox"];

const inputCls = "h-11 w-full rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";
const textareaCls = "w-full rounded-lg border border-line p-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

const fieldKey = (label) =>
  label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function ImageField({ value, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vendor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" hidden onChange={upload} data-testid="vproduct-image-input" />
      {value ? (
        <div className="relative inline-block">
          <img src={fileUrl(value)} alt="" className="h-32 w-32 rounded-xl border border-line object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-white"><X className="h-3 w-3" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} data-testid="vproduct-image-btn"
          className="flex h-32 w-32 flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-line text-slate-400 hover:border-brand hover:text-brand">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ImagePlus className="h-6 w-6" /><span className="text-[11px] font-bold">Image</span></>}
        </button>
      )}
    </div>
  );
}

export default function VendorProductForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const refresh = useVendorRefresh();
  const { data: vendorMe } = useVendorMe();
  const [showPreview, setShowPreview] = useState(false);
  const { data: categories = [] } = useCategories();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(!editing);

  useEffect(() => {
    if (editing) {
      api.get(`/vendor/products/${id}`).then(({ data }) => {
        setForm({
          ...EMPTY, ...data,
          price: data.price || "", old_price: data.old_price || "",
          tags: (data.tags || []).join(", "),
          compatibility: data.compatibility || [],
          features: data.features || [],
          custom_specs: data.custom_specs || [],
          required_fields: data.required_fields || [],
          promo_ends_at: data.promo_ends_at ? data.promo_ends_at.slice(0, 16) : "",
        });
        setReady(true);
        if (!data.product_type) {
          api.get(`/vendor/product-deliveries/by-product/${id}`).then(({ data: pd }) => {
            if (pd && pd.id) setForm((f) => ({ ...f, product_type: f.product_type || "digital" }));
          }).catch(() => {});
        }
      }).catch(() => { toast.error("Produit introuvable"); navigate("/vendeur/produits"); });
    }
  }, [id, editing, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const appendLink = (key) => (token) => setForm((f) => ({ ...f, [key]: `${f[key] ? `${f[key]} ` : ""}${token}` }));
  const appendButton = (key) => (token) => setForm((f) => ({ ...f, [key]: `${f[key] ? `${f[key]}\n` : ""}${token}` }));

  const featuresText = useMemo(
    () => (Array.isArray(form.features) ? form.features.join("\n") : form.features || ""),
    [form.features],
  );

  const submit = async (e, publish) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      price: parseInt(form.price || 0, 10),
      old_price: parseInt(form.old_price || 0, 10),
      devices: parseInt(form.devices || 1, 10),
      manual_stock: parseInt(form.manual_stock || 0, 10),
      features: Array.isArray(form.features) ? form.features.filter(Boolean) : String(form.features).split("\n").filter(Boolean),
      compatibility: Array.isArray(form.compatibility)
        ? form.compatibility.map((s) => String(s).trim()).filter(Boolean)
        : String(form.compatibility).split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      promo_ends_at: form.promo_ends_at ? new Date(form.promo_ends_at).toISOString() : "",
      is_promo: parseInt(form.old_price || 0, 10) > parseInt(form.price || 0, 10),
      required_fields: (form.required_fields || [])
        .filter((f) => f.label?.trim())
        .map((f) => ({
          key: f.key || fieldKey(f.label),
          label: f.label.trim(),
          type: f.type || "text",
          placeholder: f.placeholder || "",
          required: f.required !== false,
          help: f.help || "",
          options: NEEDS_OPTIONS.includes(f.type) ? (f.options || []).map((o) => String(o).trim()).filter(Boolean) : [],
        })),
      custom_specs: (form.custom_specs || [])
        .filter((s) => s.label?.trim())
        .map((s) => ({ label: s.label.trim(), value: s.value || "" })),
      status: publish ? "published" : (form.status === "published" ? "published" : "draft"),
    };
    try {
      if (editing) await api.put(`/vendor/products/${id}`, payload);
      else await api.post("/vendor/products", payload);
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      refresh(["vendor-products", "vendor-dashboard"]);
      navigate("/vendeur/produits");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  if (!ready) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-5" data-testid="vendor-product-form">
      <button onClick={() => navigate("/vendeur/produits")} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-brand"><ArrowLeft className="h-4 w-4" /> Retour</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-ink">{editing ? "Modifier le produit" : "Ajouter un produit"}</h1>
        <button
          type="button"
          data-testid="vp-preview-btn"
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 rounded-full border border-brand/30 bg-brand-soft px-5 py-2.5 text-sm font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white"
        >
          <Eye className="h-4 w-4" /> Aperçu client
        </button>
      </div>
      <ProductPreview open={showPreview} onClose={() => setShowPreview(false)} form={form} shopName={vendorMe?.shop_name} />

      <form onSubmit={(e) => submit(e, false)} className="space-y-5">
        <div className="space-y-4 rounded-2xl border border-line bg-white p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom du produit *">
              <input data-testid="vp-name" required value={form.name} onChange={set("name")} className={inputCls} />
            </Field>
            <Field label="URL (slug)" hint="Laisser vide pour générer automatiquement">
              <input data-testid="vp-slug" value={form.slug || ""} onChange={set("slug")} className={inputCls} />
            </Field>
          </div>

          <Field label="Image du produit">
            <ImageField value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} />
          </Field>

          <Field label="Description courte" hint="Vous pouvez insérer des liens cliquables et des boutons">
            <input data-testid="vp-short" value={form.short_description} onChange={set("short_description")} className={inputCls} />
            <div className="flex flex-wrap gap-4">
              <LinkInserter testId="vp-short" onInsert={appendLink("short_description")} />
              <ButtonInserter testId="vp-short" onInsert={appendButton("short_description")} />
            </div>
          </Field>

          <Field label="Description complète" hint="Liens, images et boutons s'affichent dans l'onglet Description">
            <textarea data-testid="vp-desc" rows={4} value={form.description} onChange={set("description")} className={textareaCls} />
            <div className="flex flex-wrap gap-4">
              <LinkInserter testId="vp-desc" onInsert={appendLink("description")} />
              <ImageInserter testId="vp-desc" uploadUrl="/vendor/upload" onInsert={(token) => setForm((f) => ({ ...f, description: `${f.description ? `${f.description}\n` : ""}${token}` }))} />
              <ButtonInserter testId="vp-desc" onInsert={appendButton("description")} />
            </div>
          </Field>

          <Field label="Caractéristiques (une par ligne)">
            <textarea data-testid="vp-features" rows={4} value={featuresText}
              onChange={(e) => setForm((f) => ({ ...f, features: e.target.value.split("\n") }))} className={textareaCls} />
          </Field>

          <Field label="Informations détaillées (libellé / valeur)" hint="S'affichent dans le tableau d'informations de la fiche produit">
            <div className="space-y-2" data-testid="vp-specs">
              {(form.custom_specs || []).map((spec, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input data-testid={`vp-spec-label-${index}`} value={spec.label} placeholder="Libellé (ex : Format)"
                    onChange={(e) => setForm((f) => { const next = [...f.custom_specs]; next[index] = { ...next[index], label: e.target.value }; return { ...f, custom_specs: next }; })}
                    className={inputCls} />
                  <input data-testid={`vp-spec-value-${index}`} value={spec.value} placeholder="Valeur (ex : ISO 64 bits)"
                    onChange={(e) => setForm((f) => { const next = [...f.custom_specs]; next[index] = { ...next[index], value: e.target.value }; return { ...f, custom_specs: next }; })}
                    className={inputCls} />
                  <button type="button" data-testid={`vp-spec-remove-${index}`} title="Retirer"
                    onClick={() => setForm((f) => ({ ...f, custom_specs: f.custom_specs.filter((_, i) => i !== index) }))}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line text-slate-400 hover:border-rose-300 hover:text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" data-testid="vp-spec-add"
                onClick={() => setForm((f) => ({ ...f, custom_specs: [...(f.custom_specs || []), { label: "", value: "" }] }))}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand">
                <Plus className="h-3.5 w-3.5" /> Ajouter une information
              </button>
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Catégorie">
              <select data-testid="vp-category" value={form.category_slug} onChange={set("category_slug")} className={inputCls}>
                <option value="">— Choisir —</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Prix (FCFA) *">
              <input data-testid="vp-price" required type="number" min="0" value={form.price} onChange={set("price")} className={inputCls} />
            </Field>
            <Field label="Ancien prix (FCFA)" hint="0 = pas de promotion">
              <input data-testid="vp-oldprice" type="number" min="0" value={form.old_price} onChange={set("old_price")} className={inputCls} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Type de licence">
              <input data-testid="vp-type" value={form.license_type} onChange={set("license_type")} className={inputCls} />
            </Field>
            <Field label="Durée">
              <input data-testid="vp-duration" value={form.duration} onChange={set("duration")} className={inputCls} />
            </Field>
            <Field label="Nombre d'appareils">
              <input data-testid="vp-devices" type="number" min="1" value={form.devices} onChange={set("devices")} className={inputCls} />
            </Field>
          </div>

          <Field label="Provenance / type de licence">
            <textarea data-testid="vp-origin" rows={2} value={form.license_origin} onChange={set("license_origin")} className={textareaCls} />
            <LinkInserter testId="vp-origin" onInsert={appendLink("license_origin")} />
            <ButtonInserter testId="vp-origin" onInsert={appendButton("license_origin")} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Compatibilité (séparée par des virgules)">
              <input data-testid="vp-compatibility"
                value={Array.isArray(form.compatibility) ? form.compatibility.join(", ") : form.compatibility}
                onChange={(e) => setForm((f) => ({ ...f, compatibility: e.target.value.split(",") }))} className={inputCls} />
            </Field>
            <Field label="Mots-clés (tags)">
              <input data-testid="vp-tags" value={form.tags} onChange={set("tags")} className={inputCls} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Plateforme">
              <select data-testid="vp-platform" value={form.platform} onChange={set("platform")} className={inputCls}>
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Mode de livraison">
              <select data-testid="vp-delivery-mode" value={form.delivery_mode} onChange={set("delivery_mode")} className={inputCls}>
                {DELIVERY.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </Field>
            <Field label="Fin de promotion">
              <input data-testid="vp-promo-end" type="datetime-local" value={form.promo_ends_at} onChange={set("promo_ends_at")} className={inputCls} />
            </Field>
          </div>

          <Field label="Instructions de livraison">
            <textarea data-testid="vp-delivery" rows={2} value={form.delivery_instructions} onChange={set("delivery_instructions")} className={textareaCls} />
            <LinkInserter testId="vp-instructions" onInsert={appendLink("delivery_instructions")} />
            <ButtonInserter testId="vp-instructions" onInsert={appendButton("delivery_instructions")} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Badge de service" hint="Ex : Service Serveur">
              <input data-testid="vp-service-label" value={form.service_label} onChange={set("service_label")} className={inputCls} placeholder="Service Serveur" />
            </Field>
            <Field label="Délai de livraison" hint="Ex : 1 à 60 minutes">
              <input data-testid="vp-delivery-time" value={form.delivery_time} onChange={set("delivery_time")} className={inputCls} placeholder="1 à 60 minutes" />
            </Field>
          </div>

          <div className="rounded-xl border border-line bg-surface p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-ink">Champs à demander au client</p>
                <p className="text-[11px] text-slate-500">Le client remplira ces champs avant de commander (ex : Nom d'utilisateur, IMEI...).</p>
              </div>
              <button type="button" data-testid="vp-add-field"
                onClick={() => setForm((f) => ({ ...f, required_fields: [...(f.required_fields || []), { label: "", type: "text", required: true, placeholder: "", key: "", options: [] }] }))}
                className="flex items-center gap-1.5 rounded-lg border border-brand px-3 py-2 text-xs font-bold text-brand hover:bg-brand-soft">
                <Plus className="h-3.5 w-3.5" /> Ajouter un champ
              </button>
            </div>
            <div className="space-y-2" data-testid="vp-fields-list">
              {(form.required_fields || []).map((field, index) => (
                <div key={index} className="grid gap-2 rounded-lg border border-line bg-white p-3 sm:grid-cols-[1fr_1fr_130px_auto_auto]">
                  <input data-testid={`vp-field-label-${index}`} placeholder="Libellé (ex : IMEI)" value={field.label || ""}
                    onChange={(e) => setForm((f) => ({ ...f, required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, label: e.target.value } : x)) }))}
                    className="h-10 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none" />
                  <input placeholder="Placeholder (optionnel)" value={field.placeholder || ""}
                    onChange={(e) => setForm((f) => ({ ...f, required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, placeholder: e.target.value } : x)) }))}
                    className="h-10 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none" />
                  <select data-testid={`vp-field-type-${index}`} value={field.type || "text"}
                    onChange={(e) => setForm((f) => ({ ...f, required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, type: e.target.value } : x)) }))}
                    className="h-10 rounded-lg border border-line px-2 text-xs font-semibold">
                    {FIELD_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                    <input type="checkbox" data-testid={`vp-field-required-${index}`} checked={field.required !== false}
                      onChange={(e) => setForm((f) => ({ ...f, required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, required: e.target.checked } : x)) }))}
                      className="h-4 w-4 rounded border-slate-300 text-brand" />
                    Obligatoire
                  </label>
                  <button type="button" data-testid={`vp-field-remove-${index}`}
                    onClick={() => setForm((f) => ({ ...f, required_fields: f.required_fields.filter((_, i) => i !== index) }))}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-slate-400 hover:border-rose-300 hover:text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  {NEEDS_OPTIONS.includes(field.type) && (
                    <input data-testid={`vp-field-options-${index}`}
                      placeholder="Options séparées par des virgules (ex : Rouge, Vert, Bleu)"
                      value={(field.options || []).join(", ")}
                      onChange={(e) => setForm((f) => ({ ...f, required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) } : x)) }))}
                      className="h-10 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none sm:col-span-full" />
                  )}
                </div>
              ))}
              {!(form.required_fields || []).length && (
                <p className="rounded-lg border border-dashed border-line py-4 text-center text-xs text-slate-400">
                  Aucun champ — le client commande sans informations supplémentaires.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Garantie">
              <input data-testid="vp-warranty" value={form.warranty} onChange={set("warranty")} className={inputCls} />
            </Field>
            <Field label="Stock manuel" hint="Utilisé si la gestion par clés est désactivée">
              <input data-testid="vp-manual-stock" type="number" min="0" value={form.manual_stock} onChange={set("manual_stock")} className={inputCls} />
            </Field>
          </div>

          <Field label="Conditions d'utilisation">
            <textarea data-testid="vp-conditions" rows={3} value={form.conditions} onChange={set("conditions")} className={textareaCls} />
            <LinkInserter testId="vp-conditions" onInsert={appendLink("conditions")} />
            <ButtonInserter testId="vp-conditions" onInsert={appendButton("conditions")} />
          </Field>

          <div className="space-y-3 rounded-xl border border-line bg-surface p-4" data-testid="vp-downloads-section">
            <Field label="Ce produit nécessite un téléchargement ?" hint="Choisissez un type pour préparer à l'avance les fichiers, liens, clés et instructions livrés automatiquement au client après validation du paiement.">
              <select data-testid="vp-product-type" value={form.product_type || ""} onChange={set("product_type")} className={inputCls}>
                {PRODUCT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </Field>
            {form.product_type ? <DownloadsManager productId={id} scope="vendor" /> : null}
          </div>

          <div className="flex flex-wrap gap-4 rounded-xl border border-line bg-surface p-4">
            {[
              ["is_featured", "Produit vedette"],
              ["is_subscription", "Abonnement"],
              ["stock_managed", "Gérer le stock par clés"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm font-semibold text-ink">
                <input type="checkbox" data-testid={`vp-${key}`} checked={!!form[key]} onChange={set(key)}
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button type="submit" data-testid="vp-save-draft" disabled={loading} className="rounded-full border border-line px-6 py-3 text-sm font-bold text-ink hover:border-brand disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer en brouillon"}
          </button>
          <button type="button" onClick={(e) => submit(e, true)} data-testid="vp-publish" disabled={loading} className="btn-glow rounded-full bg-brand px-8 py-3 text-sm font-bold text-white disabled:opacity-60">
            Publier
          </button>
        </div>
      </form>
    </div>
  );
}
